import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Funnel, FunnelPageInfo } from '@/types/funnel.ts';
import { Badge } from '@/components/ui/badge.tsx';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { formatRelativeDate } from '@/pages/Meta/Flow/Funnel/utils/funnelUtils.ts';
import {
    MoreVertical,
    Eye,
    BarChart3,
    Edit,
    Copy,
    Power,
    PowerOff,
    Trash2,
    Globe,
    Languages,
    GitBranch,
    ExternalLink,
    Tag,
    Loader2,
    Download,
} from 'lucide-react';

const MAX_VISIBLE_PAGES = 4;

interface FunnelGridProps {
    funnels: Funnel[];
    onView: (funnel: Funnel) => void;
    onDetails: (funnel: Funnel) => void;
    onEdit: (funnel: Funnel) => void;
    onDelete: (funnel: Funnel) => void;
    onToggleActive: (funnel: Funnel) => void;
    onDuplicate: (funnel: Funnel) => void;
    onExport: (funnel: Funnel) => void;
    pageIdToInternalId: Map<string, number>;
}

function RowMenu({
    funnel,
    onView,
    onDetails,
    onEdit,
    onDuplicate,
    onExport,
    onToggleActive,
    onDelete,
}: {
    funnel: Funnel;
    onView: () => void;
    onDetails: () => void;
    onEdit: () => void;
    onDuplicate: () => void;
    onExport: () => void;
    onToggleActive: () => void;
    onDelete: () => void;
}) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    return (
        <div ref={ref} className="relative">
            <button
                onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
                className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
            >
                <MoreVertical className="w-4 h-4" />
            </button>
            {open && (
                <div className="absolute right-0 top-8 z-20 bg-white border border-gray-100 rounded-xl shadow-lg py-1 min-w-[180px]">
                    <button
                        onClick={(e) => { e.stopPropagation(); setOpen(false); onView(); }}
                        className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                        <Eye className="w-4 h-4 text-gray-400" />
                        Ver funil
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); setOpen(false); onDetails(); }}
                        className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                        <BarChart3 className="w-4 h-4 text-gray-400" />
                        Detalhes
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); setOpen(false); onEdit(); }}
                        className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                        <Edit className="w-4 h-4 text-gray-400" />
                        Editar
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); setOpen(false); onDuplicate(); }}
                        className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                        <Copy className="w-4 h-4 text-gray-400" />
                        Duplicar
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); setOpen(false); onExport(); }}
                        className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                        <Download className="w-4 h-4 text-gray-400" />
                        Exportar JSON
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); setOpen(false); onToggleActive(); }}
                        className={`flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${funnel.is_active ? 'text-orange-600' : 'text-green-600'}`}
                    >
                        {funnel.is_active
                            ? <PowerOff className="w-4 h-4" />
                            : <Power className="w-4 h-4" />}
                        {funnel.is_active ? 'Desativar' : 'Ativar'}
                    </button>
                    <div className="border-t border-gray-100 my-1" />
                    <button
                        onClick={(e) => { e.stopPropagation(); setOpen(false); onDelete(); }}
                        className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                        <Trash2 className="w-4 h-4" />
                        Excluir
                    </button>
                </div>
            )}
        </div>
    );
}

function PagesModal({
    open,
    onOpenChange,
    funnel,
    pageIdToInternalId,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    funnel: Funnel | null;
    pageIdToInternalId: Map<string, number>;
}) {
    const navigate = useNavigate();

    if (!funnel) return null;

    const handlePageClick = (page: FunnelPageInfo) => {
        const internalId = pageIdToInternalId.get(page.page_id);
        if (internalId) {
            navigate(`/meta/connection/pages/${internalId}`);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[480px]">
                <DialogHeader>
                    <DialogTitle className="text-lg font-bold text-[#0f172a]">
                        Páginas do Funil
                    </DialogTitle>
                    <p className="text-sm text-gray-500 mt-1">{funnel.nome}</p>
                </DialogHeader>
                <div className="space-y-2 max-h-[400px] overflow-y-auto py-2">
                    {funnel.pages.map((page) => {
                        const internalId = pageIdToInternalId.get(page.page_id);
                        return (
                            <button
                                key={page.id}
                                type="button"
                                onClick={() => handlePageClick(page)}
                                className="w-full flex items-center justify-between p-3 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50/50 transition-all text-left group/page"
                            >
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="w-9 h-9 bg-[#0f172a] rounded-full flex items-center justify-center flex-shrink-0">
                                        <span className="text-white font-semibold text-xs">
                                            {(page.page_name || page.page_id).substring(0, 2).toUpperCase()}
                                        </span>
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-gray-900 truncate">
                                            {page.page_name || page.page_id}
                                        </p>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-xs text-gray-400">ID: {page.page_id}</span>
                                            {page.internal_category_name && (
                                                <span className="inline-flex items-center gap-1 text-[10px] font-medium text-purple-600 bg-purple-50 rounded px-1.5 py-0">
                                                    <Tag className="w-2.5 h-2.5" strokeWidth={1.5} />
                                                    {page.internal_category_name}
                                                </span>
                                            )}
                                            {page.language && (
                                                <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-600 bg-emerald-50 rounded px-1.5 py-0">
                                                    <Languages className="w-2.5 h-2.5" strokeWidth={1.5} />
                                                    {page.language}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                {internalId && (
                                    <ExternalLink className="w-4 h-4 text-gray-300 group-hover/page:text-blue-500 transition-colors flex-shrink-0" />
                                )}
                            </button>
                        );
                    })}
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default function FunnelGrid({
    funnels,
    onView,
    onDetails,
    onEdit,
    onDelete,
    onToggleActive,
    onDuplicate,
    onExport,
    pageIdToInternalId,
}: FunnelGridProps) {
    const [pagesModalFunnel, setPagesModalFunnel] = useState<Funnel | null>(null);

    return (
        <>
            <div className="bg-white rounded-2xl border border-gray-100 overflow-visible">
                {/* Header */}
                <div className="grid grid-cols-[1fr_100px_120px_40px] px-6 py-3 border-b border-gray-100 bg-gray-50/60 rounded-t-2xl">
                    <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Funil</span>
                    <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Nodes</span>
                    <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Criado</span>
                    <span />
                </div>

                {/* Rows */}
                <div className="divide-y divide-gray-100">
                    {funnels.map((funnel) => {
                        const pagesCount = funnel.pages.length;
                        const visiblePages = funnel.pages.slice(0, MAX_VISIBLE_PAGES);
                        const hiddenCount = pagesCount - MAX_VISIBLE_PAGES;
                        const languages = Array.from(
                            new Set(funnel.pages.map((p) => p.language).filter(Boolean))
                        );
                        const categories = Array.from(
                            new Set(funnel.pages.map((p) => p.internal_category_name).filter(Boolean))
                        );

                        return (
                            <div
                                key={funnel.id}
                                className="group/row relative grid grid-cols-[1fr_100px_120px_40px] px-6 py-4 cursor-pointer items-center transition-all duration-150 hover:bg-gradient-to-r hover:from-slate-50 hover:to-transparent"
                                onClick={() => onView(funnel)}
                            >
                                {/* Left accent on hover */}
                                <span className="absolute left-0 top-0 h-full w-[3px] rounded-r bg-blue-400 opacity-0 group-hover/row:opacity-100 transition-opacity duration-150" />

                                {/* Funil */}
                                <div className="pr-6 min-w-0">
                                    <div className="mb-1">
                                        <Badge
                                            className={`border rounded-full text-[11px] px-2 py-0 font-medium ${
                                                funnel.is_active
                                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200/50'
                                                    : 'bg-gray-50 text-gray-500 border-gray-200/50'
                                            }`}
                                        >
                                            {funnel.is_active ? 'Ativo' : 'Inativo'}
                                        </Badge>
                                        {funnel.ai_translate && (() => {
                                            const ts = funnel.translation_status;
                                            const tsStatus = ts?.status;
                                            if (tsStatus === 'running' || tsStatus === 'queued') {
                                                const done = parseInt(ts?.pages_done || '0', 10);
                                                const total = parseInt(ts?.pages_to_translate || ts?.pages_total || '0', 10);
                                                return (
                                                    <Badge className="ml-1.5 border rounded-full text-[11px] px-2 py-0 font-medium bg-amber-50 text-amber-700 border-amber-200/50 gap-1">
                                                        <Loader2 className="w-3 h-3 animate-spin" />
                                                        {total > 0 ? `Traduzindo ${done}/${total}` : 'Traduzindo...'}
                                                    </Badge>
                                                );
                                            }
                                            return (
                                                <Badge className="ml-1.5 border rounded-full text-[11px] px-2 py-0 font-medium bg-cyan-50 text-cyan-700 border-cyan-200/50">
                                                    AI Translate
                                                </Badge>
                                            );
                                        })()}
                                    </div>
                                    <p className="text-[15px] font-bold text-gray-900 truncate mb-1.5 transition-colors group-hover/row:text-gray-700">
                                        {funnel.nome}
                                    </p>
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        {/* Page tags (max 4) */}
                                        {visiblePages.map((p) => (
                                            <span
                                                key={p.id}
                                                className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-600 bg-blue-50 border border-blue-100/50 rounded-md px-2 py-0.5"
                                            >
                                                <Globe className="w-3 h-3 text-blue-400" strokeWidth={1.5} />
                                                {p.page_name || p.page_id}
                                            </span>
                                        ))}

                                        {/* +N counter */}
                                        {hiddenCount > 0 && (
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setPagesModalFunnel(funnel);
                                                }}
                                                className="inline-flex items-center text-[11px] font-semibold text-violet-600 bg-violet-50 border border-violet-100/50 rounded-md px-2 py-0.5 hover:bg-violet-100 transition-colors cursor-pointer"
                                            >
                                                +{hiddenCount}
                                            </button>
                                        )}

                                        {/* Category tags */}
                                        {categories.map((cat, i) => (
                                            <span
                                                key={`cat-${i}`}
                                                className="inline-flex items-center gap-1 text-[11px] font-medium text-purple-600 bg-purple-50 border border-purple-100/50 rounded-md px-2 py-0.5"
                                            >
                                                <Tag className="w-3 h-3 text-purple-400" strokeWidth={1.5} />
                                                {cat}
                                            </span>
                                        ))}

                                        {/* Language tags */}
                                        {languages.map((lang, i) => (
                                            <span
                                                key={`lang-${i}`}
                                                className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-600 bg-emerald-50 border border-emerald-100/50 rounded-md px-2 py-0.5"
                                            >
                                                <Languages className="w-3 h-3 text-emerald-400" strokeWidth={1.5} />
                                                {lang}
                                            </span>
                                        ))}

                                        {pagesCount === 0 && (
                                            <span className="text-[11px] text-gray-400 italic">Sem páginas</span>
                                        )}
                                    </div>
                                </div>

                                {/* Nodes */}
                                <div className="flex items-center gap-2">
                                    <GitBranch className="w-4 h-4 text-gray-400" strokeWidth={1.5} />
                                    <span className="text-sm font-semibold text-gray-800">
                                        {funnel.total_nodes ?? '—'}
                                    </span>
                                </div>

                                {/* Criado */}
                                <div className="text-sm text-gray-500">
                                    {formatRelativeDate(funnel.created_at)}
                                </div>

                                {/* Menu */}
                                <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
                                    <RowMenu
                                        funnel={funnel}
                                        onView={() => onView(funnel)}
                                        onDetails={() => onDetails(funnel)}
                                        onEdit={() => onEdit(funnel)}
                                        onDuplicate={() => onDuplicate(funnel)}
                                        onExport={() => onExport(funnel)}
                                        onToggleActive={() => onToggleActive(funnel)}
                                        onDelete={() => onDelete(funnel)}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Modal de páginas */}
            <PagesModal
                open={!!pagesModalFunnel}
                onOpenChange={(open) => { if (!open) setPagesModalFunnel(null); }}
                funnel={pagesModalFunnel}
                pageIdToInternalId={pageIdToInternalId}
            />
        </>
    );
}
